import {
  EMAIL_MAX_LENGTH,
  PROJECT_NAME_MAX_LENGTH,
  PROJECT_PREFIX_MAX_LENGTH,
  TEAM_TITLE_MAX_LENGTH,
  TITLE_MAX_LENGTH
} from "@/helpers/validationHelper"
import { DefinitionOfDone, ResourceType, userInviteSuccessStatuses } from "@/models"
import { $t } from "@/plugins/i18n"
import { Actions, store } from "@/store"

import { debounceWithThrottle } from "./debounceWithThrottle"
import { parsingUrlName, replaceDashToSpace, toLowerCaseWithTrim } from "./helpers"

export type ValidationResultField = "existsByUrlName" | "existsByName"

interface ValidationPayload {
  resourceTypes: ResourceType
  checkByField?: string
  validationId?: string
  checkResultField?: string
  projectId?: string
  includeDemo?: boolean
  notUnique?: boolean
}

interface ValidationParams {
  value: string
  resourceTypes: ResourceType
  checkByField?: string
  validationId?: string
  checkResultField?: ValidationResultField
  projectId?: string
  includeDemo?: boolean
}

export const isRequired = (value, data?) => {
  const text = value
  const validate = (value, text?) => {
    const stringValue = String(value)
    const isNumber = typeof value === "number"
    if ((value || isNumber) && stringValue && stringValue.trim()) {
      return true
    }
    return $t("validatorsMessage.required", [text]) || $t("This is required")
  }
  return data?.form ? validate(value, data.field) : value => validate(value, text)
}

export const checkTokenInput = (tokenSelected: boolean) => {
  return (value: string): boolean | string => {
    if (!value?.trim()) {
      return "Token is required"
    }

    if (tokenSelected) {
      return "Existing Token Detected"
    }

    return true
  }
}

export const validateDefinition = (enabledDefinitions: DefinitionOfDone[]) => {
  return (value: string): boolean | string => {
    const trimmed = value?.trim() ?? ""

    if (!trimmed) {
      return "Definition is required"
    }

    if (trimmed.length > 50) {
      return "Definition must be 50 characters or less"
    }

    const exists = enabledDefinitions.some(
      def => def.definition.trim().toLowerCase() === trimmed.toLowerCase()
    )

    if (exists) {
      return "Definition already exists"
    }

    return true
  }
}

const isTooLong = (resourceTypes, value) =>
  (resourceTypes === "ACCOUNT" && value.length > PROJECT_NAME_MAX_LENGTH) ||
  (resourceTypes === "TEAM" && value.length > TEAM_TITLE_MAX_LENGTH) ||
  (resourceTypes === "PROJECT" && value.length > PROJECT_NAME_MAX_LENGTH) ||
  value.length > TITLE_MAX_LENGTH

const prohibitedCharactersPattern = /[!@#$%^&*()_+=[\]{};:'"\\|,.<>/?~`]+|\s+$/

const validation = async (payload, value, text?, required?) => {
  const { resourceTypes, projectId, includeDemo, checkByField, checkResultField, validationId } =
    payload

  if (value && value.trim()) {
    if (isTooLong(resourceTypes, value)) {
      return $t("validatorsMessage.tooLong", [text]) || $t("Too long")
    }
    if (resourceTypes === "TEAM" && prohibitedCharactersPattern.test(value)) {
      return `${text} contains prohibited characters`
    }
    if (payload.notUnique) {
      return true
    }
    const lowerCaseValue = toLowerCaseWithTrim(value ?? "")

    const callName = validationId ?? "callField"
    if (!searchCalls[callName]) {
      searchCalls[callName] = debounceWithThrottle(makeSearchCall, 150)
    }
    const res = await searchCalls[callName]({
      value,
      resourceTypes,
      projectId,
      includeDemo,
      checkByField
    })
    const alreadyExistMessage =
      (resourceTypes === "TEAM" && `${value} already used`) ||
      $t("validatorsMessage.exists", [text]) ||
      "Already exists"
    if (
      (res &&
        res.length &&
        res.find(
          e =>
            replaceDashToSpace(toLowerCaseWithTrim(e.textPreview ?? "")) ===
            replaceDashToSpace(lowerCaseValue)
        )) ||
      res[checkResultField]
    ) {
      return alreadyExistMessage
    }
    return true
  }
  if (required) {
    return $t("validatorsMessage.required", [text]) || "This is required"
  }
}

export const personNameValidate = fieldName => {
  return value => {
    if (!value) {
      return `${fieldName} is required`
    }

    if (value.length > PROJECT_NAME_MAX_LENGTH) {
      return `${fieldName} is too long`
    }

    if (prohibitedCharactersPattern.test(value)) {
      return `${fieldName} contains prohibited characters`
    }

    return true
  }
}

export const prohibitedCharacters = fieldName => {
  return value => {
    if (value?.length > PROJECT_NAME_MAX_LENGTH) {
      return `${fieldName} is too long`
    }

    if (prohibitedCharactersPattern.test(value)) {
      return `${fieldName} contains prohibited characters`
    }

    return true
  }
}

export const isRequiredAndValid = (
  payload: ValidationPayload,
  value: string,
  data?,
  skipValid?: boolean
) => {
  if (skipValid) {
    return isRequired(value, data)
  }
  const text = value
  return data?.form
    ? validation(payload, value, true)
    : value => validation(payload, value, text, true)
}

export const isValid = (resourceType: ResourceType, value: string, data?) => {
  const text = value
  return data?.form
    ? validation(resourceType, value)
    : value => validation(resourceType, value, text)
}

const searchCalls = {}

function makeSearchCall(params: ValidationParams) {
  return new Promise(resolve => {
    const { resourceTypes, value } = params
    switch (resourceTypes) {
      case "PROJECT":
        resolve(
          store.dispatch(Actions.CHECK_EXIST_PROJECT_BY_URL_OR_NAME, {
            name: value.toLowerCase().trim(),
            checkByField: params.checkByField
          })
        )
        break
      case "ACCOUNT":
        resolve(store.dispatch(Actions.CHECK_ACCOUNT_NAME, parsingUrlName(value)))
        break
      case "TEAM":
        resolve(store.dispatch(Actions.CHECK_TEAM_NAME, value.toLowerCase()))
        break
      default:
        resolve(
          store.dispatch(Actions.SEARCH_RESOURCE, {
            text: value.toLowerCase().trim(),
            resourceTypes,
            projectId: params.projectId,
            includeDemo: params.includeDemo
          })
        )
        break
    }
  })
}

const makeTestApiCall = async value =>
  new Promise(resolve => {
    setTimeout(() => {
      resolve(value === "a@a.a" ? true : false)
    }, 500)
  })

export const validateEmail = async value => {
  const res = await makeTestApiCall(value)
  return res ? true : "This email is already taken"
}

export const validateUtilization = value => {
  if (value && value.trim()) {
    if (value.replace("%", "") > 0) {
      return true
    }
    return "Utilization should be > 0"
  }
  return "Utilization is required"
}

export const validateProjectPrefix = value => {
  if (value && value.trim()) {
    if (value.length > 0 && value.length <= PROJECT_PREFIX_MAX_LENGTH) {
      return true
    }
    return $t("Prefix too long")
  }
  return "Prefix is required"
}

export const isEmail = (value, data?) => {
  const text = value

  const validate = (value, _text?) => {
    const stringValue = String(value ?? "")

    if (!stringValue.trim()) {
      return "Email address is required"
    }

    if (stringValue.length > EMAIL_MAX_LENGTH) {
      return "Invalid email address"
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (emailRegex.test(stringValue.toLowerCase())) {
      return true
    }

    return "Invalid email address"
  }

  return data?.form ? validate(value, data.field) : value => validate(value, text)
}

export const isEmailAndDuplicate = (value, { existingEmails, sendJiraEmails }) => {
  if (!value) {
    return "Required"
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(String(value).toLowerCase())) {
    return "Invalid"
  }

  const emailCount = existingEmails.filter(
    email => email.toLowerCase() === value.toLowerCase()
  ).length
  if (emailCount > 1) {
    return "Duplicated"
  }

  if (sendJiraEmails && sendJiraEmails.length) {
    const emailInSendEmails = sendJiraEmails.find(
      email => email.inviteeEmail.toLowerCase() === value.toLowerCase()
    )

    if (emailInSendEmails && !userInviteSuccessStatuses.includes(emailInSendEmails.checkResult)) {
      return "Email already exists"
    }
  }

  return true
}

export const isUrl = (value, data?) => {
  const text = value

  const validate = (value, _text?) => {
    if (!value || !value.trim()) {
      return true
    }

    const urlRegex =
      /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/

    if (!urlRegex.test(value)) {
      return "Please enter a valid URL"
    }

    return true
  }

  return data?.form ? validate(value, data.field) : value => validate(value, text)
}

export default {
  isRequired,
  isRequiredAndValid,
  validateEmail,
  validateUtilization,
  isValid,
  isEmail,
  isEmailAndDuplicate,
  validateProjectPrefix,
  personNameValidate,
  prohibitedCharacters,
  checkTokenInput,
  isUrl,
  validateDefinition
}
